// @flow

import * as React from 'react';
import {useContext} from 'react';
import {useLocation} from 'docs/data';

import {contextStubFunction} from 'docs/util';

import {initialState, reducer} from './reducer';
import {
  setFocus,
  addPane,
  removePane,
  focusPane,
  open,
  close,
  toggleMinimize,
  invokeCommand,
  updatePane,
  reset,
  lockPanes,
  unlockPanes,
  printOutput,
} from './actions';

import type {
  State,
  FocusAction,
  RemovePaneAction,
  FocusPaneAction,
  InvokeCommandAction,
  UpdatePaneAction,
  PrintOutputAction,
} from './reducer';

type StripeCluiContextType = {
  state: State,
  setFocus: ($PropertyType<FocusAction, 'payload'>) => void,
  addPane: () => void,
  removePane: ($PropertyType<RemovePaneAction, 'payload'>) => void,
  focusPane: ($PropertyType<FocusPaneAction, 'payload'>) => void,
  open: () => void,
  close: () => void,
  toggleMinimize: () => void,
  invokeCommand: ($PropertyType<InvokeCommandAction, 'payload'>) => void,
  updatePane: ($PropertyType<UpdatePaneAction, 'payload'>) => void,
  reset: () => void,
  lockPanes: () => void,
  unlockPanes: () => void,
  printOutput: ($PropertyType<PrintOutputAction, 'payload'>) => void,
};

export const StripeCluiContext = React.createContext<StripeCluiContextType>({
  state: initialState,
  setFocus: contextStubFunction('StripeClui'),
  addPane: contextStubFunction('StripeClui'),
  removePane: contextStubFunction('StripeClui'),
  focusPane: contextStubFunction('StripeClui'),
  open: contextStubFunction('StripeClui'),
  close: contextStubFunction('StripeClui'),
  toggleMinimize: contextStubFunction('StripeClui'),
  invokeCommand: contextStubFunction('StripeClui'),
  updatePane: contextStubFunction('StripeClui'),
  reset: contextStubFunction('StripeClui'),
  lockPanes: contextStubFunction('StripeClui'),
  unlockPanes: contextStubFunction('StripeClui'),
  printOutput: contextStubFunction('StripeClui'),
});

type StripeCluiProviderProps = {
  children?: React.Node,
};

export const StripeCluiProvider = (props: StripeCluiProviderProps) => {
  const [state, dispatch] = React.useReducer(reducer, initialState);
  const {location, replaceLocation} = useLocation();

  const context = {
    state,
    setFocus: setFocus(dispatch),
    addPane: addPane(dispatch),
    removePane: removePane(dispatch),
    focusPane: focusPane(dispatch),
    open: open(dispatch, location, replaceLocation),
    close: close(dispatch, location, replaceLocation),
    toggleMinimize: toggleMinimize(dispatch),
    invokeCommand: invokeCommand(dispatch),
    updatePane: updatePane(dispatch),
    reset: reset(dispatch),
    lockPanes: lockPanes(dispatch),
    unlockPanes: unlockPanes(dispatch),
    printOutput: printOutput(dispatch),
  };

  return (
    <StripeCluiContext.Provider value={context}>
      {props.children}
    </StripeCluiContext.Provider>
  );
};

export const useStripeClui = () => {
  return useContext(StripeCluiContext);
};

export default StripeCluiProvider;
